import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** True once brm:add-success has opened the drawer for the current add. */
  #openedOnSuccess = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('brm:add-success', this.#handleAddSuccess);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('brm:add-success', this.#handleAddSuccess);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Opens the drawer once the add has finished. If the add came from a modal that is still
   * open (quick add), wait for its native close so focus restoration runs first.
   * @param {HTMLDialogElement | null} sourceModal
   */
  #openDrawer(sourceModal) {
    const drawer = this.#themeDrawer;
    if (!drawer || drawer.isOpen) return;

    if (sourceModal?.open) {
      sourceModal.addEventListener(
        'close',
        () => {
          if (!drawer.isOpen) drawer.open();
        },
        { once: true }
      );
    } else {
      drawer.open();
    }
  }

  /**
   * Fallback path (empty cart, batch adds): open when the cart promise resolves.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isAdd = event.action === 'add';
    if (isAdd) this.#openedOnSuccess = false;

    const shouldAutoOpen = this.hasAttribute('auto-open') && isAdd;
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(({ detail }) => {
        // Already opened at add-success: don't reopen a drawer the shopper may have closed since.
        if (shouldAutoOpen && !detail?.didError && !this.#openedOnSuccess) {
          this.#openDrawer(sourceModal);
        }
        requestAnimationFrame(() => this.#updateStickyState());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * add.js returned and the drawer was already morphed with the new item: open now,
   * without waiting for /cart.json. Skipped for an empty cart so the empty state never flashes.
   */
  #handleAddSuccess = (event) => {
    if (!this.hasAttribute('auto-open') || this.#isCartEmpty()) return;
    const modal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );
    this.#openedOnSuccess = true;
    this.#openDrawer(modal);
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}